import React from "react";
import { getCustomizationsRoot } from "../util/commonFunctions";
import SpanishBanner from "./dynamic-sections/SpanishBanner";

import { ReactComponent as FaceBookIcon } from "../assets/icons/facebook.svg";
import { ReactComponent as LinkedInIcon } from "../assets/icons/linkedin.svg";
import { ReactComponent as InstagramIcon } from "../assets/icons/instagram.svg";
import { ReactComponent as TwitterIcon } from "../assets/icons/twitter.svg";

export default function HeroSection({ currentUser }) {
  const details = currentUser?.details;
  const branding = currentUser?.branding;
  const customizations = getCustomizationsRoot(currentUser);
  const templateTwoBackgroundUrl = customizations?.templateTwoBackgroundUrl?.trim?.() || "";
  const hasTemplateTwoBackground = Boolean(templateTwoBackgroundUrl);

  const primaryColor = branding?.primaryColor ? `#${branding.primaryColor}` : "#1B3A4B";
  const primaryButtonColor = branding?.primaryButtonColor ? `#${branding.primaryButtonColor}` : "#9A1B64";
  const secondaryButtonColor = branding?.secondaryButtonColor ? `#${branding.secondaryButtonColor}` : "#5F88A9";
  const applicationUrl = details?.Encompass_Application_URL__c || details?.GA_Encompass_Application_URL__c || "";
  const bookingUrl = customizations?.options?.bookingLink;
  const templateTwoLogoUrl = branding?.LogoUrl || branding?.logoUrl || branding?.whiteLogoUrl || "";
  const showSpanishBanner = Boolean(customizations?.options?.habloEspBanner);

  const socialIconStyle = { width: "22px", height: "22px", fill: "white" };

  if (hasTemplateTwoBackground) {
    const darkTextColor = primaryColor;
    const mutedTextColor = "#374151";
    const socialIconStyleOnLight = { width: "22px", height: "22px", fill: darkTextColor };
    const heroBackgroundStyle = {
      backgroundColor: "#ffffff",
      backgroundImage: `linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.62) 52%, rgba(255, 255, 255, 0.88) 76%, #ffffff 100%), url(${templateTwoBackgroundUrl})`,
      backgroundPosition: "center center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
    };

    return (
      <section className="w-full overflow-hidden bg-white" style={heroBackgroundStyle}>
        <div className="mx-auto flex min-h-[620px] w-full max-w-[1440px] flex-col justify-center px-6 py-12 md:min-h-[700px] md:px-10 lg:px-16">
          <div className="grid items-center gap-10 md:grid-cols-[minmax(320px,460px)_minmax(0,1fr)] md:gap-12 lg:gap-16">
            <div className="flex flex-col items-center justify-center py-8 md:py-12">
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-auto h-[320px] w-[320px] rounded-full bg-white/70 blur-[2px] md:h-[400px] md:w-[400px]" />
                {details?.Website_Headshot__c ? (
                  <div className="relative z-10 h-72 w-72 overflow-hidden rounded-full border-[10px] border-white/80 bg-white shadow-[0_24px_70px_rgba(27,58,75,0.18)] md:h-[360px] md:w-[360px] lg:h-[420px] lg:w-[420px]">
                    <img
                      src={details.Website_Headshot__c}
                      alt={`${details?.FirstName || ""} ${details?.LastName || ""}`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                ) : (
                  <div className="relative z-10 h-72 w-72 rounded-full border-[10px] border-white/70 bg-white/80 shadow-[0_24px_70px_rgba(27,58,75,0.12)] md:h-[360px] md:w-[360px] lg:h-[420px] lg:w-[420px]" />
                )}
              </div>
              {showSpanishBanner ? (
                <div className="mt-6 w-72 md:w-[360px] lg:w-[420px]">
                  <SpanishBanner backgroundColor={branding?.secondaryColor} />
                </div>
              ) : null}
            </div>

            <div className="relative z-10 flex flex-col items-center text-center md:items-start md:text-left">
              {templateTwoLogoUrl ? (
                <img
                  src={templateTwoLogoUrl}
                  alt={branding?.name || "Logo"}
                  className="mb-6 h-16 w-auto max-w-full md:h-20 lg:h-24"
                />
              ) : null}

              <h1 className="text-4xl font-bold text-[#244b6a] md:text-5xl lg:text-7xl">
                {details?.FirstName} {details?.LastName}
              </h1>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-lg md:justify-start md:text-[1.85rem]">
                {details?.Title ? <span style={{ color: mutedTextColor }}>{details.Title}</span> : null}
                {details?.Title && details?.LoanForce__NMLS_Number__c ? (
                  <span className="text-slate-400">|</span>
                ) : null}
                {details?.LoanForce__NMLS_Number__c ? (
                  <span style={{ color: mutedTextColor }}>NMLS# {details.LoanForce__NMLS_Number__c}</span>
                ) : null}
              </div>

              <div className="mt-8 flex items-center gap-4">
                {details?.Facebook__c ? (
                  <a
                    href={details.Facebook__c}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors hover:bg-white/60"
                    style={{ borderColor: `${darkTextColor}80` }}
                  >
                    <FaceBookIcon style={socialIconStyleOnLight} />
                  </a>
                ) : null}
                {details?.LinkedIn__c ? (
                  <a
                    href={details.LinkedIn__c}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors hover:bg-white/60"
                    style={{ borderColor: `${darkTextColor}80` }}
                  >
                    <LinkedInIcon style={socialIconStyleOnLight} />
                  </a>
                ) : null}
                {details?.Twitter__c ? (
                  <a
                    href={details.Twitter__c}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors hover:bg-white/60"
                    style={{ borderColor: `${darkTextColor}80` }}
                  >
                    <TwitterIcon style={socialIconStyleOnLight} />
                  </a>
                ) : null}
                {details?.Instagram__c ? (
                  <a
                    href={details.Instagram__c}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors hover:bg-white/60"
                    style={{ borderColor: `${darkTextColor}80` }}
                  >
                    <InstagramIcon style={socialIconStyleOnLight} />
                  </a>
                ) : null}
              </div>

              <div className="mt-8 flex w-full flex-col items-center gap-4 md:w-auto md:flex-row md:justify-start">
                {applicationUrl ? (
                  <a
                    href={applicationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full rounded-md px-6 py-3 text-center text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 md:w-auto md:min-w-[180px]"
                    style={{ backgroundColor: primaryButtonColor }}
                  >
                    Apply Now
                  </a>
                ) : null}
                {bookingUrl ? (
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full rounded-md px-6 py-3 text-center text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 md:w-auto md:min-w-[180px]"
                    style={{ backgroundColor: secondaryButtonColor }}
                  >
                    Book Appointment
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full overflow-hidden">
      <div className="flex flex-col-reverse md:flex-row">
        {/* Left — Headshot with decorative circle, overlaps into right panel */}
        <div className="flex flex-col items-center justify-center bg-slate-100 py-14 md:w-[42%] md:py-12">
          <div className="relative flex items-center justify-center">
            {details?.Website_Headshot__c ? (
              <div className="relative z-10 md:translate-x-[15%] h-72 w-72 md:h-[340px] md:w-[340px] lg:h-[380px] lg:w-[380px]">
                {/* Half-circle background — left side, slightly larger, solid fill behind photo */}
                <div
                  className="absolute inset-[-14px] md:inset-[-18px] rounded-full z-[-1]"
                  style={{ backgroundColor: primaryColor, clipPath: "inset(0 50% 0 0)" }}
                />
                <div className="h-full w-full overflow-hidden rounded-full bg-slate-200">
                  <img
                    src={details.Website_Headshot__c}
                    alt={`${details?.FirstName || ""} ${details?.LastName || ""}`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            ) : (
              <div className="h-72 md:h-[360px]" />
            )}
          </div>
          {showSpanishBanner ? (
            <div className="mt-6 w-72 px-4 md:w-[340px] md:translate-x-[15%] md:px-0 lg:w-[380px]">
              <SpanishBanner backgroundColor={branding?.secondaryColor} />
            </div>
          ) : null}
        </div>

        {/* Right — Info panel */}
        <div
          className="flex flex-1 flex-col items-center justify-center px-8 py-16 text-center md:items-start md:px-12 md:py-24 md:text-left lg:px-16"
          style={{ backgroundColor: primaryColor }}
        >
          {branding?.whiteLogoUrl ? (
            <img
              src={branding.whiteLogoUrl}
              alt={branding?.name || "Logo"}
              className="mb-6 h-16 w-auto md:h-20 lg:h-24"
            />
          ) : null}

          <h1 className="text-4xl font-bold italic text-white md:text-5xl lg:text-6xl">
            {details?.FirstName} {details?.LastName}
          </h1>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 text-lg text-white/90 md:justify-start">
            {details?.Title ? <span className="font-semibold">{details.Title}</span> : null}
            {details?.Title && details?.LoanForce__NMLS_Number__c ? <span className="text-white/50">|</span> : null}
            {details?.LoanForce__NMLS_Number__c ? <span>NMLS# {details.LoanForce__NMLS_Number__c}</span> : null}
          </div>

          {/* Social Icons */}
          <div className="mt-6 flex items-center gap-4">
            {details?.Facebook__c ? (
              <a
                href={details.Facebook__c}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white/60 transition-colors hover:border-white hover:bg-white/10"
              >
                <FaceBookIcon style={socialIconStyle} />
              </a>
            ) : null}
            {details?.LinkedIn__c ? (
              <a
                href={details.LinkedIn__c}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white/60 transition-colors hover:border-white hover:bg-white/10"
              >
                <LinkedInIcon style={socialIconStyle} />
              </a>
            ) : null}
            {details?.Twitter__c ? (
              <a
                href={details.Twitter__c}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white/60 transition-colors hover:border-white hover:bg-white/10"
              >
                <TwitterIcon style={socialIconStyle} />
              </a>
            ) : null}
            {details?.Instagram__c ? (
              <a
                href={details.Instagram__c}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white/60 transition-colors hover:border-white hover:bg-white/10"
              >
                <InstagramIcon style={socialIconStyle} />
              </a>
            ) : null}
          </div>

          {/* Buttons */}
          <div className="mt-8 flex w-full flex-col items-center gap-4 md:w-auto md:flex-row md:justify-start">
            {applicationUrl ? (
              <a
                href={applicationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full rounded-md px-6 py-3 text-center text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 md:w-auto md:min-w-[180px]"
                style={{ backgroundColor: primaryButtonColor }}
              >
                Apply Now
              </a>
            ) : null}
            {bookingUrl ? (
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full rounded-md px-6 py-3 text-center text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 md:w-auto md:min-w-[180px]"
                style={{ backgroundColor: secondaryButtonColor }}
              >
                Book Appointment
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
